import type { Event, Order, OrderItem, Transaction, User } from '@/lib/api/types'
import type { OrderItem as ReceiptItem, ReceiptData } from './types'

const buildUserName = (user?: User | null): string | undefined => {
  if (!user) return undefined
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim()
  return name || user.email || user.phone_number || user.phone || undefined
}

const mapOrderItems = (items: OrderItem[] = []): ReceiptItem[] => {
  return items.map((item) => {
    const vendorItem = typeof item.vendor_event_item === 'object' ? item.vendor_event_item : null
    const qty = item.qty || item.quantity || 1
    const total = item.total || 0
    const fallbackPrice = qty > 0 ? Math.round(total / qty) : total

    return {
      name: vendorItem?.name || item.name || 'Item',
      qty,
      price: item.price_at_ordertime ?? item.price ?? vendorItem?.price ?? fallbackPrice,
    }
  })
}

const sumItemQty = (items: OrderItem[] = []): number => {
  return items.reduce((sum, item) => sum + (item.qty || item.quantity || 0), 0)
}

export const buildOrderReceiptData = (order: Order, event?: Event | null): ReceiptData => {
  const vendorEvent = typeof order.vendor_event === 'object' ? order.vendor_event : null
  const vendorName = vendorEvent?.vendor?.name
  const orderUser = typeof order.user === 'object' ? order.user as User : null
  const createdAt = order.created_at || order.created
  const orderItems = order.order_items || order.items || []

  return {
    orderCode: order.order_display_id || order.order_number || String(order.id),
    orderTime: new Date(createdAt || Date.now()),
    customerName: buildUserName(orderUser),
    items: mapOrderItems(orderItems),
    totalItems: sumItemQty(orderItems),
    totalTokens: order.total || order.order_total || 0,
    vendorName,
    eventName: event?.name,
  }
}

export const buildTransactionReceiptData = (transaction: Transaction, event?: Event | null): ReceiptData => {
  const transactionOrder = transaction.order
  const vendorName = transactionOrder?.vendor_event?.vendor?.name
  const timestamp = transaction.timestamp || transaction.created

  const primaryUser = transaction.to_user || transaction.user || transaction.from_user || null

  if (transactionOrder) {
    const orderItems = transactionOrder.order_items || []
    return {
      orderCode: transactionOrder.order_display_id || String(transactionOrder.id),
      orderTime: new Date(transactionOrder.created_at || timestamp || Date.now()),
      customerName: buildUserName(primaryUser),
      items: mapOrderItems(orderItems),
      totalItems: sumItemQty(orderItems),
      totalTokens: transactionOrder.total || transactionOrder.order_total || transaction.amount || 0,
      vendorName,
      eventName: event?.name,
    }
  }

  const actionLabel = transaction.action || 'Transaction'
  const tokenAmount = Math.round(transaction.amount || 0)
  const lineItemName = actionLabel
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()

  return {
    orderCode: `TX-${transaction.id}`,
    orderTime: new Date(timestamp || Date.now()),
    customerName: buildUserName(primaryUser),
    items: [
      {
        name: lineItemName || 'Transaction',
        qty: 1,
        price: tokenAmount,
      },
    ],
    totalItems: 1,
    totalTokens: tokenAmount,
    note: transaction.description,
    vendorName,
    eventName: event?.name,
  }
}

const formatDateTime = (value?: string) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString()
}

const formatEventAddress = (event?: Event | null) => {
  if (!event) return ''
  const parts = [
    event.address,
    [event.city, event.zip_code].filter(Boolean).join(', ').replace(/,\s+,/g, ', '),
    event.country,
  ].filter(Boolean)
  return parts.join(' ')
}

export const buildOrderReceiptHtml = (order: Order, event?: Event | null) => {
  const vendorEvent = typeof order.vendor_event === 'object' ? order.vendor_event : null
  const vendorName = vendorEvent?.vendor?.name
  const boothName = vendorEvent?.booth || vendorName
  const orderUser = typeof order.user === 'object' ? order.user as User : null
  const createdAt = order.created_at || order.created
  const orderItems = order.order_items || order.items || []

  const itemsHtml = orderItems.map((item) => {
    const vendorItem = typeof item.vendor_event_item === 'object' ? item.vendor_event_item : null
    const qty = item.qty || item.quantity || 1
    const total = item.total || 0
    return `
      <div class="item">
        <div class="row">
          <span class="item-name">${vendorItem?.name || item.name || 'Item'}</span>
          <span>${Math.round(total)} Tokens</span>
        </div>
        <div class="row">
          <span>${qty} x ${Math.round(item.price_at_ordertime ?? item.price ?? vendorItem?.price ?? (qty ? total / qty : total))} Tokens</span>
          <span></span>
        </div>
      </div>
    `
  }).join('')

  const addressLine = formatEventAddress(event)

  return `
    <div class="header">
      <div class="title">${event?.name || 'EventPay'}</div>
      ${addressLine ? `<div class="subtitle">${addressLine}</div>` : ''}
      ${vendorName ? `<div class="subtitle">${vendorName}${boothName && boothName !== vendorName ? ` - ${boothName}` : ''}</div>` : ''}
    </div>
    <div class="section">
      <div class="row"><span>Order #</span><span>${order.order_display_id || order.order_number || order.id}</span></div>
      <div class="row"><span>Date</span><span>${formatDateTime(createdAt)}</span></div>
      ${orderUser ? `<div class="row"><span>Customer</span><span>${buildUserName(orderUser) || ''}</span></div>` : ''}
    </div>
    <div class="divider"></div>
    <div class="section items">
      ${itemsHtml || '<div class="item">No items</div>'}
    </div>
    <div class="divider"></div>
    <div class="section">
      <div class="row total"><span>Total</span><span>${Math.round(order.total || order.order_total || 0)} Tokens</span></div>
    </div>
  `
}

export const buildTransactionReceiptHtml = (transaction: Transaction, event?: Event | null) => {
  const transactionOrder = transaction.order
  const vendorName = transactionOrder?.vendor_event?.vendor?.name
  const timestamp = transaction.timestamp || transaction.created
  const primaryUser = transaction.to_user || transaction.user || transaction.from_user || null

  const addressLine = formatEventAddress(event)

  if (transactionOrder) {
    const orderItems = transactionOrder.order_items || []
    const baseTokens = transactionOrder.total || transactionOrder.order_total || transaction.amount || 0
    const signedTokens = transaction.action === 'refund' ? -Math.abs(baseTokens) : Math.abs(baseTokens)
    const signedTokensLabel = signedTokens < 0
      ? `${signedTokens} Tokens`
      : `${Math.abs(signedTokens)} Tokens`
    const itemsHtml = orderItems.map((item) => {
      const vendorItem = typeof item.vendor_event_item === 'object' ? item.vendor_event_item : null
      const qty = item.qty || item.quantity || 1
      const total = item.total || 0
      return `
        <div class="item">
          <div class="row">
            <span class="item-name">${vendorItem?.name || item.name || 'Item'}</span>
            <span>${Math.round(total)} Tokens</span>
          </div>
          <div class="row">
            <span>${qty} x ${Math.round(item.price_at_ordertime ?? item.price ?? vendorItem?.price ?? (qty ? total / qty : total))} Tokens</span>
            <span></span>
          </div>
        </div>
      `
    }).join('')

    return `
      <div class="header">
        <div class="title">${event?.name || 'EventPay'}</div>
        ${addressLine ? `<div class="subtitle">${addressLine}</div>` : ''}
        ${vendorName ? `<div class="subtitle">${vendorName}</div>` : ''}
      </div>
      <div class="section">
        <div class="row"><span>Order #</span><span>${transactionOrder.order_display_id || transactionOrder.id}</span></div>
        <div class="row"><span>Date</span><span>${formatDateTime(transactionOrder.created_at || timestamp)}</span></div>
        ${primaryUser ? `<div class="row"><span>Customer</span><span>${buildUserName(primaryUser) || ''}</span></div>` : ''}
      </div>
      <div class="divider"></div>
      <div class="section items">
        ${itemsHtml || '<div class="item">No items</div>'}
      </div>
      <div class="divider"></div>
      <div class="section">
        <div class="row total"><span>Total</span><span>${signedTokensLabel}</span></div>
      </div>
    `
  }

  const actionLabel = transaction.action || 'Transaction'
  const tokenAmount = Math.round(transaction.amount || 0)
  const lineItemName = actionLabel.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()
  const tokenValue = event?.token_value || 0
  const basePrice = tokenValue ? tokenAmount * tokenValue : 0
  const taxAmount = transaction.tax_amount || 0
  const feeAmount = transaction.additional_fee_amount || 0
  const totalCharge = transaction.total_amount || (basePrice + taxAmount + feeAmount)
  const showBreakdown = transaction.action === 'addCredit' && tokenValue > 0

  return `
    <div class="header">
      <div class="title">${event?.name || 'EventPay'}</div>
      ${addressLine ? `<div class="subtitle">${addressLine}</div>` : ''}
      ${vendorName ? `<div class="subtitle">${vendorName}</div>` : ''}
    </div>
    <div class="section">
      <div class="row"><span>Transaction #</span><span>${transaction.id}</span></div>
      <div class="row"><span>Date</span><span>${formatDateTime(timestamp)}</span></div>
      ${primaryUser ? `<div class="row"><span>Customer</span><span>${buildUserName(primaryUser) || ''}</span></div>` : ''}
    </div>
    <div class="divider"></div>
    <div class="section items">
      <div class="item">
        <div class="row">
          <span class="item-name">${lineItemName || 'Transaction'}</span>
          <span>${tokenAmount} Tokens</span>
        </div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="section">
      <div class="row total"><span>Total</span><span>${tokenAmount} Tokens</span></div>
      ${showBreakdown ? `
        <div class="divider"></div>
        <div class="row"><span>${tokenAmount} Tokens</span><span>$${basePrice.toFixed(2)}</span></div>
        <div class="row"><span>Tax</span><span>$${taxAmount.toFixed(2)}</span></div>
        <div class="row"><span>Fee</span><span>$${feeAmount.toFixed(2)}</span></div>
        <div class="row total"><span>Total Charge</span><span>$${totalCharge.toFixed(2)}</span></div>
      ` : ''}
    </div>
  `
}
